import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled from 'styled-components';

const { Text } = Typography;

const FieldContainer = styled.div`
  cursor: pointer;

  &:hover .edit-icon {
    opacity: 1;
  }
`;

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  align-items: center;
`;

const EditIcon = styled(EditOutlined)`
  opacity: 0;
  transition: opacity ${({ theme }) => theme.motionDurationMid};
  color: ${({ theme }) => theme.colorTextTertiary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-left: ${({ theme }) => theme.marginXS}px;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: center;
`;

export interface EditableFieldProps<T = string> {
  value: T;
  onSave: (value: T) => Promise<void>;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  emptyText?: string;
  showIcon?: boolean;
  formatter?: (value: T) => string;
  parser?: (text: string) => T;
}

export function EditableField<T = string>({
  value,
  onSave,
  placeholder = 'Enter value...',
  multiline = false,
  disabled = false,
  emptyText = 'Click to add',
  showIcon = true,
  formatter = (v: T) => String(v),
  parser = (text: string) => text as T,
}: EditableFieldProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = () => {
    if (disabled) return;
    setEditValue(formatter(value));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const saveValue = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onSave(parser(editValue.trim()));
      setIsEditing(false);
    } catch (error) {
      // Error handling is handled by the parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      void saveValue();
    }
    if (e.key === 'Enter' && multiline && !e.shiftKey) {
      e.preventDefault();
      void saveValue();
    }
    if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  if (isEditing) {
    const InputComponent = multiline ? Input.TextArea : Input;

    return (
      <EditContainer>
        <InputComponent
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          autoFocus
          disabled={isSaving}
          {...(multiline ? { autoSize: { minRows: 2, maxRows: 6 } } : {})}
        />
        <EditActions>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={saveValue}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={cancelEditing}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </EditActions>
      </EditContainer>
    );
  }

  return (
    <FieldContainer onClick={startEditing}>
      <ValueContainer>
        {value ? (
          <Text style={{ lineHeight: 1.6 }}>{formatter(value)}</Text>
        ) : (
          <Text type="secondary" italic>
            {emptyText}
          </Text>
        )}
        {showIcon && !disabled && <EditIcon className="edit-icon" />}
      </ValueContainer>
    </FieldContainer>
  );
}

export default EditableField;
